import { useEffect, useRef } from 'react';

/**
 * useKeyboardShortcut — generic global keyboard-combo listener.
 *
 * Slice 8 PR#1. Reusable across features (the calculator wires
 * `mod+k`; future features can wire `mod+shift+k`, etc.).
 *
 * `mod` maps to ⌘ on macOS and Ctrl elsewhere, matching the hint
 * already shown in the TopBar search box. The handler is stored in
 * a ref so callers can pass an inline closure without re-binding the
 * `window` listener on every render.
 *
 * The listener calls `preventDefault()` on a match so the browser's
 * default (e.g. Chrome address-bar focus on Ctrl+K) is suppressed.
 */
export type ShortcutCombo = 'mod+k' | 'mod+shift+k';

interface ParsedCombo {
  key: string;
  shift: boolean;
}

function parseCombo(combo: ShortcutCombo): ParsedCombo {
  const parts = combo.toLowerCase().split('+');
  return {
    key: parts[parts.length - 1] ?? '',
    shift: parts.includes('shift'),
  };
}

export function useKeyboardShortcut(
  combo: ShortcutCombo,
  handler: () => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const { key, shift } = parseCombo(combo);

    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      if (event.key.toLowerCase() !== key) return;
      if (shift !== event.shiftKey) return;
      event.preventDefault();
      handlerRef.current();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [combo, enabled]);
}
